/*  iceberg-ui  */
import { Button } from '@auviknetworks/iceberg-ui';

/*  MUI  */
import { Alert, Box, CircularProgress } from '@mui/material';

/*  Shared  */
import { Transition } from 'application/Shared/Helpers';

/*  React  */
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Modal, ModalBody, ModalFooter } from './Components';

interface ConfirmProps {
    'data-testid'?: string;
    'data-trackid'?: string;
    dividers?: boolean;
    loading?: boolean;
    modalOpen: boolean;
    promptAlertIcon?: boolean;
    promptAlertText?: string;
    promptAlertTextType?: 'error' | 'info' | 'success' | 'warning';
    promptCancelFn?: () => void;
    promptConfirmFn: () => void;
    promptText: string | JSX.Element;
    promptTitle: string;
    textCancelButton?: string;
    textConfirmButton?: string;
}

/**
 * A confirmation modal component that displays a dialog with customizable content and actions
 * @param {Object} props - Component props
 * @param {string} [props.dataTestid] - data test id
 * @param {string} [props.dataTrackid] - data track id
 * @param {boolean} [props.dividers] - Whether to show dividers in the modal body
 * @param {boolean} [props.loading] - Loading state that shows a progress indicator
 * @param {boolean} props.modalOpen - Initial open state of the modal
 * @param {boolean} [props.promptAlertIcon] - Whether to show an icon in the alert message
 * @param {string} [props.promptAlertText] - Text to display in the alert message
 * @param {string} [props.promptAlertTextType] - Severity type of the alert ('info', 'warning', 'error', 'success')
 * @param {Function} [props.promptCancelFn] - Callback function when cancel button is clicked
 * @param {Function} props.promptConfirmFn - Callback function when confirm button is clicked
 * @param {string|React.ReactNode} props.promptText - Main text content of the modal
 * @param {string} props.promptTitle - Title of the modal
 * @param {string} [props.textCancelButton] - Custom text for cancel button
 * @param {string} [props.textConfirmButton] - Custom text for confirm button
 * @returns {React.ReactElement} Confirmation modal component
 */
export const Confirm = ({
    'data-testid': dataTestid = 'confirmModal',
    'data-trackid': dataTrackid = 'confirmModal',
    dividers,
    loading,
    modalOpen,
    promptAlertIcon = false,
    promptAlertText,
    promptAlertTextType,
    promptCancelFn,
    promptConfirmFn,
    promptText,
    promptTitle,
    textCancelButton,
    textConfirmButton
}: ConfirmProps): React.ReactElement => {
    const { t } = useTranslation();
    const [open, setOpen] = useState(modalOpen);

    const handleClose = async (close: boolean) => {
        if (close) {
            await promptConfirmFn();
        } else if (promptCancelFn) {
            promptCancelFn();
        }
        setOpen(false);
    };

    return (
        <Modal
            aria-labelledby="confirm-action-dialog"
            data-testid={dataTestid}
            onClose={() => handleClose(false)}
            open={open}
            title={promptTitle}
            TransitionComponent={Transition}
        >
            <ModalBody>
                {promptAlertText && (
                    <Alert
                        data-testid={`${dataTestid}Alert`}
                        {...(promptAlertIcon ? {} : { icon: false })}
                        severity={promptAlertTextType || 'info'}
                        sx={{ marginBottom: '16px' }}
                    >
                        {promptAlertText}
                    </Alert>
                )}
                {promptText}
                {dividers && <hr data-testid="confirmModalDivider" />}
            </ModalBody>
            <ModalFooter>
                <Button
                    data-testid={`${dataTestid}CancelBtn`}
                    data-trackid={`${dataTrackid}CancelBtn`}
                    onClick={() => handleClose(false)}
                    variant="inverted"
                >
                    {textCancelButton || t('Common.cancel')}
                </Button>
                <Box display="flex" alignItems="center">
                    {loading && (
                        <CircularProgress
                            size="32px"
                            sx={{ marginRight: '8px' }}
                        />
                    )}
                    <Button
                        data-testid={`${dataTestid}ConfirmBtn`}
                        data-trackid={`${dataTrackid}ConfirmBtn`}
                        onClick={() => handleClose(true)}
                        disabled={!!loading}
                    >
                        {textConfirmButton || t('Common.confirm')}
                    </Button>
                </Box>
            </ModalFooter>
        </Modal>
    );
};
